import React from 'react';
import { Box, Avatar, useTheme, keyframes } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';

/**
 * A component that displays a thinking animation for the chat assistant.
 * Shows animated dots in a wave pattern to indicate the assistant is processing.
 */
const ThinkingLoader: React.FC = () => {
  const theme = useTheme();
  
  // Define the wave animation for each dot
  const wave = keyframes`
    0%, 60%, 100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-6px);
    }
  `;
  
  // Common styles for the dots
  const dotStyle = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    margin: '0 3px',
    display: 'inline-block',
    backgroundColor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
  };
  
  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        px: 3,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(68, 70, 84, 0.3)' 
          : 'rgba(247, 247, 248, 0.8)',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          maxWidth: '700px',
          mx: 'auto',
          gap: 2,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
              width: 30, 
              height: 30 
            }}
          >
            <SmartToyIcon fontSize="small" />
          </Avatar>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '24px',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              p: 1.5,
              px: 2,
              borderRadius: '18px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(32, 33, 35, 0.5)' 
                : 'rgba(236, 236, 241, 0.7)',
              boxShadow: theme.palette.mode === 'dark'
                ? 'none'
                : '0 2px 6px rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* First dot with animation */}
            <Box
              sx={{
                ...dotStyle,
                animation: `${wave} 1.2s ease-in-out infinite`,
              }}
            />
            
            {/* Second dot with delayed animation */}
            <Box
              sx={{
                ...dotStyle,
                animation: `${wave} 1.2s ease-in-out 0.2s infinite`,
              }}
            />
            
            {/* Third dot with more delayed animation */}
            <Box
              sx={{
                ...dotStyle,
                animation: `${wave} 1.2s ease-in-out 0.4s infinite`,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ThinkingLoader;